/**
 * HotspotFilters — W1.2 / B8.1 / F-033
 *
 * Design-Fix (Abnahme P1, hotspots.html): der Prototyp zeigt genau EIN
 * Freitextfeld + 3 einfache <select>-Dropdowns (Status/Type/Tag) — keine
 * Segment-Pillen fuer Status/Device-Type, keinen Sort-Dropdown, keinen
 * Legacy- oder Beobachtet-Chip. Diese Komponente rendert deshalb nur noch
 * Suche + Status-Select + Device-Type-Select; das Tag-Select bleibt aus
 * historischen Gruenden in HotspotsList (dort sitzen bereits die Tags-Daten).
 */
import { useRef, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import type { HotspotsFilters, DeviceType } from './useHotspots'

type StatusFilter = 'all' | 'online' | 'warn' | 'offline'

type Props = {
  currentFilters: HotspotsFilters
}

const STATUS_OPTION_KEYS: StatusFilter[] = ['all', 'online', 'warn', 'offline']
const DEVICE_TYPE_OPTION_KEYS: (DeviceType | 'all')[] = ['all', 'iot', 'cbrs', 'wifi']

export function HotspotFilters({ currentFilters }: Props) {
  const { t } = useTranslation('hotspots')
  const navigate = useNavigate()
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const currentStatus: StatusFilter = (currentFilters.status as StatusFilter) ?? 'all'
  const currentQ = currentFilters.q ?? ''
  const currentDeviceType: DeviceType | 'all' = (currentFilters.device_type as DeviceType | 'all') ?? 'all'

  const updateSearch = useCallback(
    (patch: Partial<HotspotsFilters>) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(navigate as any)({
        search: (prev: Record<string, unknown>) => ({ ...prev, ...patch, page: 1 }),
      })
    },
    [navigate],
  )

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      updateSearch({ q: value || undefined })
    }, 300)
  }

  function handleStatusChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const key = e.target.value as StatusFilter
    updateSearch({ status: key === 'all' ? undefined : key })
  }

  function handleDeviceTypeChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const key = e.target.value as DeviceType | 'all'
    updateSearch({ device_type: key === 'all' ? undefined : key })
  }

  return (
    <div style={{ display: 'contents' }} role="search" aria-label={t('filters.groupLabel')}>
      <input
        className="input search-input"
        type="search"
        placeholder={t('filters.searchPlaceholder')}
        defaultValue={currentQ}
        onChange={handleSearchChange}
        aria-label={t('filters.searchLabel')}
      />
      <select
        className="select"
        value={currentStatus}
        onChange={handleStatusChange}
        aria-label={t('filters.statusGroupLabel')}
      >
        {STATUS_OPTION_KEYS.map((key) => (
          <option key={key} value={key}>
            {t(`filters.chips.${key}`)}
          </option>
        ))}
      </select>
      <select
        className="select"
        value={currentDeviceType}
        onChange={handleDeviceTypeChange}
        aria-label={t('filters.deviceGroupLabel')}
      >
        {DEVICE_TYPE_OPTION_KEYS.map((key) => (
          <option key={key} value={key}>
            {t(`filters.deviceChips.${key}`)}
          </option>
        ))}
      </select>
    </div>
  )
}
